import React, { useState, useEffect } from "react";
import apiClient from "../../../../../api/apiClient";
import { CITIES_BY_STATE } from "../shared/usCities";

const US_STATES = [
    { abbr: "AL", name: "Alabama" }, { abbr: "AK", name: "Alaska" },
    { abbr: "AZ", name: "Arizona" }, { abbr: "AR", name: "Arkansas" },
    { abbr: "CA", name: "California" }, { abbr: "CO", name: "Colorado" },
    { abbr: "CT", name: "Connecticut" }, { abbr: "DE", name: "Delaware" },
    { abbr: "FL", name: "Florida" }, { abbr: "GA", name: "Georgia" },
    { abbr: "HI", name: "Hawaii" }, { abbr: "ID", name: "Idaho" },
    { abbr: "IL", name: "Illinois" }, { abbr: "IN", name: "Indiana" },
    { abbr: "IA", name: "Iowa" }, { abbr: "KS", name: "Kansas" },
    { abbr: "KY", name: "Kentucky" }, { abbr: "LA", name: "Louisiana" },
    { abbr: "ME", name: "Maine" }, { abbr: "MD", name: "Maryland" },
    { abbr: "MA", name: "Massachusetts" }, { abbr: "MI", name: "Michigan" },
    { abbr: "MN", name: "Minnesota" }, { abbr: "MS", name: "Mississippi" },
    { abbr: "MO", name: "Missouri" }, { abbr: "MT", name: "Montana" },
    { abbr: "NE", name: "Nebraska" }, { abbr: "NV", name: "Nevada" },
    { abbr: "NH", name: "New Hampshire" }, { abbr: "NJ", name: "New Jersey" },
    { abbr: "NM", name: "New Mexico" }, { abbr: "NY", name: "New York" },
    { abbr: "NC", name: "North Carolina" }, { abbr: "ND", name: "North Dakota" },
    { abbr: "OH", name: "Ohio" }, { abbr: "OK", name: "Oklahoma" },
    { abbr: "OR", name: "Oregon" }, { abbr: "PA", name: "Pennsylvania" },
    { abbr: "RI", name: "Rhode Island" }, { abbr: "SC", name: "South Carolina" },
    { abbr: "SD", name: "South Dakota" }, { abbr: "TN", name: "Tennessee" },
    { abbr: "TX", name: "Texas" }, { abbr: "UT", name: "Utah" },
    { abbr: "VT", name: "Vermont" }, { abbr: "VA", name: "Virginia" },
    { abbr: "WA", name: "Washington" }, { abbr: "WV", name: "West Virginia" },
    { abbr: "WI", name: "Wisconsin" }, { abbr: "WY", name: "Wyoming" },
    { abbr: "DC", name: "District of Columbia" },
];

const LEAD_TYPES = [
    { value: "",               label: "— Select type —" },
    { value: "retailer_brand", label: "Retailer / Brand" },
    { value: "grower",         label: "Grower" },
    { value: "consumer",       label: "Consumer" },
];

const STATUSES = [
    { value: "new",       label: "New" },
    { value: "active",    label: "Active" },
    { value: "won",       label: "Won" },
    { value: "lost",      label: "Lost" },
    { value: "converted", label: "Converted" },
];

const PRIORITIES = [
    { value: "cold", label: "Cold" },
    { value: "warm", label: "Warm" },
    { value: "hot",  label: "Hot" },
];

const EMPTY = { firstName: "", lastName: "", email: "", state: "", city: "", leadType: "", status: "new", priority: "cold" };

const Req = () => <span className="lf-req" aria-hidden="true">*</span>;

const InboundLeadFormModal = ({ lead, onClose, onSaved, baseEndpoint = "/admin/crm/inbound/leads" }) => {
    const [form, setForm]           = useState(lead ? { ...EMPTY, ...lead } : { ...EMPTY });
    const [saving, setSaving]       = useState(false);
    const [loadingFull, setLoadingFull] = useState(!!lead);
    const [error, setError]         = useState("");

    useEffect(() => {
        if (!lead) return;
        let cancelled = false;
        apiClient.get(`${baseEndpoint}/${lead.id}`).then(res => {
            if (cancelled) return;
            const full = res.data.lead || res.data;
            setForm({ ...EMPTY, ...full, state: full.state || "", city: full.city || "", leadType: full.leadType || "", status: full.status || "new", priority: full.priority || "cold" });
            setLoadingFull(false);
        }).catch(() => { if (!cancelled) setLoadingFull(false); });
        return () => { cancelled = true; };
    }, [lead?.id]);

    const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));

    const handleStateChange = e => setForm(f => ({ ...f, state: e.target.value, city: "" }));

    const handleSubmit = async e => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = {
                firstName: form.firstName,
                lastName:  form.lastName,
                email:     form.email,
                state:     form.state    || null,
                city:      form.city     || null,
                leadType:  form.leadType || null,
                status:    form.status,
                priority:  form.priority,
            };
            if (lead) {
                await apiClient.put(`${baseEndpoint}/${lead.id}`, payload);
            } else {
                await apiClient.post(baseEndpoint, payload);
            }
            onSaved();
        } catch (err) {
            setError(err.response?.data?.error || "Failed to save lead");
        } finally {
            setSaving(false);
        }
    };

    const citiesForState = form.state ? (CITIES_BY_STATE[form.state] || []) : [];

    return (
        <div className="crm-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="crm-modal crm-modal--lead-form">
                <div className="crm-modal__header">
                    <h2 className="crm-modal__title">{lead ? "Edit Lead" : "New Lead"}</h2>
                    <button className="crm-modal__close" onClick={onClose} type="button">×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="crm-modal__body">
                        {loadingFull && (
                            <div className="lf-loading">
                                <span className="lf-loading__spinner" />
                                Loading lead data…
                            </div>
                        )}
                        {error && <div className="lf-error">{error}</div>}

                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>First Name <Req /></label>
                                <input className="admin-input" required value={form.firstName} onChange={set("firstName")} maxLength={100} />
                            </div>
                            <div className="admin-form-group">
                                <label>Last Name <Req /></label>
                                <input className="admin-input" required value={form.lastName} onChange={set("lastName")} maxLength={100} />
                            </div>
                        </div>

                        <div className="admin-form-group">
                            <label>Email <Req /></label>
                            <input className="admin-input" required type="email" value={form.email} onChange={set("email")} />
                        </div>

                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>State</label>
                                <select className="admin-input" value={form.state} onChange={handleStateChange}>
                                    <option value="">— Select state —</option>
                                    {US_STATES.map(s => <option key={s.abbr} value={s.abbr}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>City</label>
                                {form.state ? (
                                    <select className="admin-input" value={form.city} onChange={set("city")}>
                                        <option value="">— Select city —</option>
                                        {citiesForState.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                ) : (
                                    <div className="lf-city-placeholder">Select a state first</div>
                                )}
                            </div>
                        </div>

                        <div className="admin-form-group">
                            <label>Lead Type</label>
                            <select className="admin-input" value={form.leadType} onChange={set("leadType")}>
                                {LEAD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>

                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>Status</label>
                                <select className="admin-input" value={form.status} onChange={set("status")}>
                                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Priority</label>
                                <select className="admin-input" value={form.priority} onChange={set("priority")}>
                                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="crm-modal__footer">
                        <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="admin-btn admin-btn--primary" disabled={saving || loadingFull}>
                            {saving ? "Saving…" : lead ? "Save Changes" : "Create Lead"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InboundLeadFormModal;
